import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  PlayArrow as TestIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { FaRobot } from "react-icons/fa";
import apiClient from "../../services/apiClient";
import toast from "react-hot-toast";
import PageLoader from "../../components/Loading/PageLoader";

const AssistantConfiguration = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const userId = useSelector((state) => state.userInfo?.user?.id);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [modelConfigs, setModelConfigs] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [transcriberConfigs, setTranscriberConfigs] = useState([]);
  const [loadingTranscribers, setLoadingTranscribers] = useState(false);
  const [analysisConfigs, setAnalysisConfigs] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [voiceConfigs, setVoiceConfigs] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [tools, setTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
  const [loadingKnowledgeBase, setLoadingKnowledgeBase] = useState(false);
  const [selectedKnowledgeBaseFiles, setSelectedKnowledgeBaseFiles] = useState([]);


  const [formData, setFormData] = useState({
    name: "",
    description: "",
    modelProvider: "openai",
    model: "gpt-4-turbo",
    llm_config_id: "",
    systemPrompt: "",
    temperature: 0.7,
    maxTokens: 1000,
    topP: 1,
    frequencyPenalty: 0,
    presencePenalty: 0,
    stopSequences: [],
    // Voice Configuration
    voiceProvider: "elevenlabs",
    voiceId: "",
    voice_id: "",
    voiceName: "",
    voiceSpeed: 1,
    voicePitch: 0,
    voiceStability: 0.5,
    // Transcriber Configuration
    transcriberProvider: "deepgram",
    transcriber_id: "",
    language: "en",
    transcriberModel: "nova",
    is_punctuation_enabled: true,
    is_profanity_filter_enabled: true,
    keywords: [],
    endpointingThreshold: 500,
    // Tools
    selectedTools: [],
    // Analysis
    analysis_id: "",
    enableSummary: true,
    enableSentiment: true,
    enableTopics: true,
    enableActionItems: true,
    summaryTemplate: "",
    // Advanced Settings
    hipaaCompliance: false,
    pciCompliance: false,
    recordingFormat: "mp3",
    recordingQuality: "high",
    dataRetention: "30",
    maxCallDuration: 30,
    silenceTimeout: 10,
    endCallPhrases: [],
    // Widget Configuration
    sources: [], 
    widgetUrl: "", 
    widgetFile: null, 
    widgetFileName: "",
    filesToDelete: [], // Track files marked for deletion
  });

  useEffect(() => {
    fetchModels();
    fetchTranscriberConfigs();
    fetchAnalysisConfigs();
    fetchVoiceConfigs();
    fetchTools();
  }, []);

  useEffect(() => {
    if (id && modelConfigs.length && transcriberConfigs.length && voiceConfigs.length && analysisConfigs.length) {
      fetchAssistant();
    }
  }, [id, modelConfigs, transcriberConfigs, voiceConfigs, analysisConfigs]);

  useEffect(() => {
    if (activeTab === 6) {
      fetchKnowledgeBaseFiles();
    }
  }, [activeTab]);

  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const res = await apiClient.get("/llm-configs");
      if (res?.status && Array.isArray(res.data)) {
        setModelConfigs(res.data);
      } else {
        toast.error("Failed to load model configurations");
      }
    } catch (err) {
      console.error("Error fetching llm-configs:", err);
      toast.error("Error fetching models");
    } finally {
      setLoadingModels(false);
    }
  };

  useEffect(() => {
    if (!formData.model || !modelConfigs.length) return;

    const selectedModel = modelConfigs.find((m) => m.model === formData.model);
    if (selectedModel) {
      setFormData((prev) => ({
        ...prev,
        llm_config_id: selectedModel._id,
        systemPrompt: selectedModel.system_prompt || prev.systemPrompt,
        temperature: selectedModel.temperature || prev.temperature,
        maxTokens: selectedModel.max_tokens || prev.maxTokens,
      }));
    }
  }, [formData.model, modelConfigs]);

  const fetchTranscriberConfigs = async () => {
    setLoadingTranscribers(true);
    try {
      const res = await apiClient.get("/transcriber-configs");
      if (res?.status && Array.isArray(res.data)) {
        setTranscriberConfigs(res.data);
      } else {
        toast.error("Failed to load transcriber configs");
      }
    } catch (err) {
      console.error("Error fetching transcriber configs:", err);
      toast.error("Error fetching transcribers");
    } finally {
      setLoadingTranscribers(false);
    }
  };

  useEffect(() => {
    if (!formData.transcriberProvider || !transcriberConfigs.length) return;
    const selected = transcriberConfigs.find(
      (t) =>
        t.provider.toLowerCase() === formData.transcriberProvider.toLowerCase()
    );
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        transcriber_id: selected._id,
        transcriberModel: selected.model || prev.transcriberModel,
        language: selected.language || prev.language,
      }));
    }
  }, [formData.transcriberProvider, transcriberConfigs]);

  const fetchAnalysisConfigs = async () => {
    setLoadingAnalysis(true);
    try {
      const res = await apiClient.get("/analysis-configs");
      if (res?.status && Array.isArray(res.data)) {
        setAnalysisConfigs(res.data);
      } else {
        toast.error("Failed to load analysis configurations");
      }
    } catch (err) {
      console.error("Error fetching analysis configs:", err);
      toast.error("Error fetching analysis configs");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const fetchVoiceConfigs = async () => {
    setLoadingVoices(true);
    try {
      const res = await apiClient.get("/voice-configs");
      if (res?.status && Array.isArray(res.data)) {
        setVoiceConfigs(res.data);
      } else {
        toast.error("Failed to load voice configurations");
      }
    } catch (err) {
      console.error("Error fetching voice configs:", err);
      toast.error("Error fetching voice configs");
    } finally {
      setLoadingVoices(false);
    }
  };

  useEffect(() => {
    if (!formData.voiceName || !voiceConfigs.length) return;
    const selected = voiceConfigs.find(
      (v) => v.voice_name === formData.voiceName
    );
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        voice_id: selected._id,
      }));
    }
  }, [formData.voiceName, voiceConfigs]);

  const fetchTools = async () => {
    setLoadingTools(true);
    try {
      const res = await apiClient.get("/tools");
      if (res?.status && Array.isArray(res.data)) {
        setTools(res.data);
      } else {
        toast.error("Failed to load tools");
      }
    } catch (err) {
      console.error("Error fetching tools:", err);
      toast.error("Error fetching tools");
    } finally {
      setLoadingTools(false);
    }
  };

  const fetchKnowledgeBaseFiles = async () => {
    setLoadingKnowledgeBase(true);
    try {
      const response = await apiClient.get("/knowledge-base");

      if (response?.data && Array.isArray(response.data)) {
        const mappedFiles = response.data.map((item) => ({
          id: item._id,
          name: item.file_name,
          type: item.file_type,
          size: item.file_size,
          uploadedAt: item.uploaded_at,
          uploadedBy: item.uploaded_by,
          status: item.status,
          filePath: item.file_path,
        }));
        setKnowledgeBaseFiles(mappedFiles);
      } else {
        setKnowledgeBaseFiles([]);
      }
    } catch (error) {
      console.error("Error fetching knowledge base files:", error);
      setKnowledgeBaseFiles([]);
    } finally {
      setLoadingKnowledgeBase(false);
    }
  };

 const handleToolToggle = async (toolId, currentStatus) => {
  try {
    await apiClient.put(`/assistant/${toolId}`, {
      status: !currentStatus ? "active" : "inactive",
    });
    setTools(
      tools.map((t) =>
        t._id === toolId ? { ...t, status: !currentStatus } : t
      )
    );
    toast.success("Tool status updated");
  } catch (err) {
    console.error("Error updating tool:", err);
    toast.error("Error updating tool status");
  }
};

  const fetchAssistant = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/assistants/${id}`);
      const assistant = response?.data;

      // Map API response to form data
      const llmConfig = modelConfigs.find(
        (m) => m._id === assistant.llm_config_id
      );
      const transcriberConfig = transcriberConfigs.find(
        (t) => t._id === assistant.transcriber_id
      );
      const voiceConfig = voiceConfigs.find(
        (v) => v._id === assistant.voice_id
      );

      setFormData((prev) => ({
        ...prev,
        name: assistant.name || "",
        description: assistant.description || "",
        llm_config_id: assistant.llm_config_id || "",
        modelProvider: llmConfig?.provider?.toLowerCase() || "openai",
        model: llmConfig?.model || "gpt-4-turbo",
        systemPrompt: llmConfig?.system_prompt || "",
        temperature: llmConfig?.temperature || 0.7,
        maxTokens: llmConfig?.max_tokens || 1000,
        transcriber_id: assistant.transcriber_id || "",
        transcriberProvider: transcriberConfig?.provider?.toLowerCase() || "deepgram",
        transcriberModel: transcriberConfig?.model || "nova",
        language: transcriberConfig?.language || "en",
        is_punctuation_enabled: transcriberConfig?.is_punctuation_enabled || true,
        is_profanity_filter_enabled: transcriberConfig?.is_profanity_filter_enabled || true,
        voice_id: assistant.voice_id || "",
        voiceProvider: voiceConfig?.provider?.toLowerCase() || "elevenlabs",
        voiceName: voiceConfig?.voice_name || "",
        analysis_id: assistant.analysis_id || "",
        selectedTools: assistant.tools || [],
        hipaaCompliance: assistant.advanced_settings?.hipaaCompliance || false,
        recordingFormat: assistant.advanced_settings?.recordingFormat || "mp3",
        dataRetention: assistant.advanced_settings?.dataRetention || "30",
        maxCallDuration: assistant.advanced_settings?.maxCallDuration || 30,
        silenceTimeout: assistant.advanced_settings?.silenceTimeout || 10,
        sources: assistant.sources || [],
        widgetUrl: assistant.widget_config?.url || "",
        widgetFileName: assistant.widget_config?.fileName || "",
        widgetFile: assistant.widget_config?.file || "",
        filesToDelete: []
      }));

      if (assistant.analysis_id) {
        setSelectedAnalysis(assistant.analysis_id);
      }

      // Update tools status if assistant has tools
      if (assistant.tools && assistant.tools.length > 0) {
        setTools((prevTools) =>
          prevTools.map((tool) => ({
            ...tool,
            status: assistant.tools.includes(tool._id),
          }))
        );
      }
    } catch (error) {
      toast.error("Failed to fetch assistant");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });


  };

  const handleSwitchChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    });
  };

  const handleSliderChange = (field) => (event, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        widgetFile: file,
        widgetFileName: file.name,
      });
    }
  };

  // Handle adding website URL to sources
  const handleAddWebsiteUrl = () => {
    if (!formData.widgetUrl.trim()) {
      toast.error("Please enter a valid URL");
      return;
    }

    // Basic URL validation
    try {
      new URL(formData.widgetUrl);
    } catch (e) {
      toast.error("Please enter a valid URL (e.g., https://example.com)");
      return;
    }

    // Check if URL already exists
    const exists = formData.sources.some(
      (source) => source.type === "website" && source.url === formData.widgetUrl
    );

    if (exists) {
      toast.error("This URL is already added");
      return;
    }

    setFormData({
      ...formData,
      sources: [
        ...formData.sources,
        {
          type: "website",
          url: formData.widgetUrl,
        },
      ],
      widgetUrl: "", // Clear input after adding
    });

    toast.success("Website URL added successfully");
  };

  // Handle removing a source (website or file)
  const handleRemoveSource = (index) => {
    const sourceToRemove = formData.sources[index];

    // If it's a file from the server, add to delete list
    if (sourceToRemove.type === "file" && sourceToRemove.path) {
      setFormData({
        ...formData,
        sources: formData.sources.filter((_, i) => i !== index),
        filesToDelete: [...formData.filesToDelete, sourceToRemove.path],
      });
    } else {
      setFormData({
        ...formData,
        sources: formData.sources.filter((_, i) => i !== index),
      });
    }
    toast.success("Source removed successfully");
  };

  // Handle viewing a file
  const handleViewFile = (source) => {
    if (source.path) {
      // Construct full URL to file
      const fileUrl = `${process.env.REACT_APP_BASE_URL}/${source.path}`;
      window.open(fileUrl, "_blank");
    }
  };

  // Handle new file upload for sources
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        widgetFile: file,
        widgetFileName: file.name,
      });
      toast.success("File selected. It will be uploaded when you save.");
    }
  };

  // Handle removing the newly selected file before upload
  const handleRemoveSelectedFile = () => {
    setFormData({
      ...formData,
      widgetFile: null,
      widgetFileName: "",
    });
    // Reset the file input so user can select a new file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    toast.success("File selection removed");
  };

  // Handle selecting files from knowledge base (single selection only)
  const handleKnowledgeBaseFileToggle = (file) => {
    const isSelected = selectedKnowledgeBaseFiles.some((f) => f.id === file.id);

    if (isSelected) {
      // Deselect - clear selection
      setSelectedKnowledgeBaseFiles([]);
    } else {
      // Select only this file - replace any previous selection
      setSelectedKnowledgeBaseFiles([file]);
    }
  };

  // Helper function to check if a file is selected
  const isKnowledgeBaseFileSelected = (fileId) => {
    return selectedKnowledgeBaseFiles.some((f) => f.id === fileId);
  };

  const validateForm = (status) => {
    // Reset all errors first

    if (!formData.name || formData.name.trim() === "") {
      toast.error("Assistant name is required");
      return false;
    }
    if (!formData.llm_config_id) {
      toast.error("Please select a model configuration");
      return false;
    }
    if (!formData.transcriber_id) {
      toast.error("Please select a transcriber configuration");
      return false;
    }

    // For active/publish status, require all fields
    if (status === "active") {
      if (!formData.voice_id) {
        toast.error("Please select a voice configuration");
        return false;
      }
      if (!formData.analysis_id) {
        toast.error("Please select an analysis configuration");
        return false;
      }
    }

    return true;
  };

  // const buildPayload = (status) => {
  //   // Get selected tool IDs (only active tools)
  //   const selectedToolIds = tools
  //     .filter((tool) => tool.status)
  //     .map((tool) => tool._id);

  //   const payload = {
  //     name: formData.name.trim(),
  //     description: formData.description.trim(),
  //     llm_config_id: formData.llm_config_id,
  //     transcriber_id: formData.transcriber_id,
  //     voice_id: formData.voice_id,
  //     analysis_id: formData.analysis_id,
  //     tools: selectedToolIds,
  //     advanced_settings: {
  //       hipaaCompliance: formData.hipaaCompliance,
  //       recordingFormat: formData.recordingFormat,
  //       dataRetention: formData.dataRetention,
  //       maxCallDuration: formData.maxCallDuration,
  //       silenceTimeout: formData.silenceTimeout,
  //     },
  //     widget_config: {
  //       url: formData.widgetUrl,
  //       fileName: formData.widgetFileName,
  //     },
  //     status: status,
  //   };

  //   // If there's a file, create FormData, otherwise return JSON
  //   if (formData.widgetFile) {
  //     const formDataPayload = new FormData();
  //     formDataPayload.append('data', JSON.stringify(payload));
  //     formDataPayload.append('file', formData.widgetFile);
  //     return formDataPayload;
  //   }

  //   return payload;
  // };


  const buildPayload = (status) => {
    const selectedToolIds = tools
      .filter((tool) => tool.status)
      .map((tool) => tool._id);

    // Extract widgetUrl from sources array (first website URL)
    const websiteSource = formData.sources.find((s) => s.type === "website");
    const widgetUrl = websiteSource?.url || "";

    return {
      name: formData.name.trim(),
      description: formData.description.trim(),
      llm_config_id: formData.llm_config_id,
      transcriber_id: formData.transcriber_id,
      voice_id: formData.voice_id,
      analysis_id: formData.analysis_id,
      selectedTools: selectedToolIds, // Changed from 'tools' to 'selectedTools'
      user_id: userId,
      widgetUrl: widgetUrl, // Extracted from sources

      advanced_settings: {
        hipaaCompliance: formData.hipaaCompliance,
        recordingFormat: formData.recordingFormat,
        dataRetention: formData.dataRetention,
        maxCallDuration: formData.maxCallDuration,
        silenceTimeout: formData.silenceTimeout,
      },

      // Keep file reference for FormData
      widgetFile: formData.widgetFile || null,

      status,
    };
  };


  const handleSave = async (status = "draft", redirectAfter = true) => {
    if (!validateForm(status)) return;

    setLoading(true);

    try {
      // Build JSON payload first
      const payload = buildPayload(status);

      // Always use FormData
      const finalPayload = new FormData();

      // Append all individual fields to FormData (matching cURL format)
      finalPayload.append("name", payload.name);
      finalPayload.append("description", payload.description);
      finalPayload.append("llm_config_id", payload.llm_config_id);
      finalPayload.append("transcriber_id", payload.transcriber_id);
      finalPayload.append("voice_id", payload.voice_id);
      finalPayload.append("analysis_id", payload.analysis_id);
      finalPayload.append("user_id", payload.user_id);
      finalPayload.append("status", payload.status);

      // Append selectedTools as JSON array string (matching cURL format)
      finalPayload.append("selectedTools", JSON.stringify(payload.selectedTools));

      // Append advanced settings as individual fields (matching cURL format)
      finalPayload.append("hipaaCompliance", payload.advanced_settings.hipaaCompliance);
      finalPayload.append("recordingFormat", payload.advanced_settings.recordingFormat);
      finalPayload.append("dataRetention", payload.advanced_settings.dataRetention);
      finalPayload.append("maxCallDuration", payload.advanced_settings.maxCallDuration);
      finalPayload.append("silenceTimeout", payload.advanced_settings.silenceTimeout);

      // Append widgetUrl (matching cURL format)
      if (payload.widgetUrl) {
        finalPayload.append("widgetUrl", payload.widgetUrl);
      }

      // Append file if exists (matching cURL format)
      if (payload.widgetFile) {
        finalPayload.append("file", payload.widgetFile);
      }



      const headers = {
        "Content-Type": "multipart/form-data",
      };

      // API call
      let response;
      if (id) {
        response = await apiClient.put(`/assistants/${id}`, finalPayload, {
          headers,
        });
      } else {
        response = await apiClient.post(`/assistants`, finalPayload, {
          headers,
        });
      }

      toast.success(`Assistant saved as ${status}`);

      // Clear the selected file after successful upload
      setFormData((prev) => ({
        ...prev,
        widgetFile: null,
        widgetFileName: "",
      }));

      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // If response contains updated sources, update the form data
      if (response?.data?.sources) {
        setFormData((prev) => ({
          ...prev,
          sources: response.data.sources,
        }));
      }

      if (redirectAfter) navigate("/assistants");

      return response?.data;

    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to save assistant");
    } finally {
      setLoading(false);
    }
  };


  const handleSaveAndTest = async () => {
    const savedAssistant = await handleSave("draft", false);
    if (savedAssistant) {
      setTestDialogOpen(true);
    }
  };

  const handleTestDialogClose = () => {
    setTestDialogOpen(false);
  };

  if (
    loading ||
    (loadingModels && !modelConfigs.length) ||
    (loadingTranscribers && !transcriberConfigs.length) ||
    (loadingVoices && !voiceConfigs.length) ||
    (loadingAnalysis && !analysisConfigs.length)
  ) {
    return <PageLoader text="Loading configuration..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          p: 3,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: "1px solid #E5E7EB",
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: "#1F2937", fontWeight: 600 }}>
            {id ? "Edit Assistant" : "Create New Assistant"}
          </Typography>
          <Typography variant="body1" sx={{ color: "#6B7280" }}>
            Configure your AI voice agent settings and capabilities
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/assistants")}
            sx={{
              borderColor: "#E5E7EB",
              color: "#6B7280",
              borderRadius: "8px",
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleSave("draft")}
            sx={{
              borderColor: "#667eea",
              color: "#667eea",
              borderRadius: "8px",
              "&:hover": {
                borderColor: "#5568d3",
                backgroundColor: "rgba(102, 126, 234, 0.04)",
              },
            }}
          >
            Save as Draft
          </Button>
          <Button
            variant="outlined"
            startIcon={<TestIcon />}
            onClick={handleSaveAndTest}
            disabled={loading}
            sx={{
              borderColor: "#667eea",
              color: "#667eea",
              borderRadius: "8px",
              "&:hover": {
                borderColor: "#5568d3",
                backgroundColor: "rgba(102, 126, 234, 0.04)",
              },
              "&:disabled": {
                borderColor: "#E5E7EB",
                color: "#9CA3AF",
              },
            }}
          >
            Save & Test
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSave("active")}
            disabled={loading}
            sx={{
              backgroundColor: "#667eea",
              color: "#FFFFFF",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#5568d3",
                boxShadow: "none",
              },
              "&:disabled": {
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
              },
            }}
          >
            Publish
          </Button>
        </Box>
      </Box>

      {/* Basic Info */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Basic Information
          </Typography>
          <TextField
            fullWidth
            label="Assistant Name"
            value={formData.name}
            onChange={handleChange("name")}
            required
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={formData.description}
            onChange={handleChange("description")}
            helperText="Describe what this assistant does"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            p: 1,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              minHeight: 48,
              borderRadius: "8px",
              mx: 0.5,
              color: "#6B7280",
              "&.Mui-selected": {
                color: "#667eea",
                backgroundColor: "#F3F4FF",
                fontWeight: 600,
              },
              "&:hover": {
                backgroundColor: "#F9FAFB",
              },
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          <Tab label="Model Configuration" />
          <Tab label="Voice" />
          <Tab label="Transcriber" />
          <Tab label="Tools" />
          <Tab label="Analysis" />
          <Tab label="Advanced Settings" />
          <Tab label="Widget" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Card
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
        }}
      >
        <CardContent sx={{ minHeight: 400, p: 3 }}>
          {/* Tab 1: Model Configuration */}
          {activeTab === 0 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Model Provider"
                    value={formData.modelProvider}
                    onChange={handleChange("modelProvider")}
                    helperText={loadingModels ? "Loading providers..." : ""}
                  >
                    {Array.from(
                      new Set(
                        modelConfigs.map((m) => m.provider.toLowerCase())
                      )
                    ).map((provider) => (
                      <MenuItem key={provider} value={provider}>
                        {provider.charAt(0).toUpperCase() + provider.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Model"
                    value={formData.model}
                    onChange={handleChange("model")}
                  >
                    {modelConfigs
                      .filter(
                        (m) =>
                          m.provider.toLowerCase() ===
                          formData.modelProvider.toLowerCase()
                      )
                      .map((model) => (
                        <MenuItem key={model._id} value={model.model}>
                          {model.name} — {model.model}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="System Prompt"
                    placeholder="You are a helpful assistant..."
                    value={formData.systemPrompt}
                    onChange={handleChange("systemPrompt")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom>
                    Temperature: {formData.temperature}
                  </Typography>
                  <Slider
                    value={formData.temperature}
                    onChange={handleSliderChange("temperature")}
                    min={0}
                    max={1}
                    step={0.1}
                    marks
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Tokens"
                    value={formData.maxTokens}
                    onChange={handleChange("maxTokens")}
                  />
                </Grid>
              </Grid>

              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Advanced Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography gutterBottom>Top P: {formData.topP}</Typography>
                      <Slider
                        value={formData.topP}
                        onChange={handleSliderChange("topP")}
                        min={0}
                        max={1}
                        step={0.1}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography gutterBottom>
                        Frequency Penalty: {formData.frequencyPenalty}
                      </Typography>
                      <Slider
                        value={formData.frequencyPenalty}
                        onChange={handleSliderChange("frequencyPenalty")}
                        min={0}
                        max={2}
                        step={0.1}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography gutterBottom>
                        Presence Penalty: {formData.presencePenalty}
                      </Typography>
                      <Slider
                        value={formData.presencePenalty}
                        onChange={handleSliderChange("presencePenalty")}
                        min={0}
                        max={2}
                        step={0.1}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          {/* Tab 2: Voice */}
          {activeTab === 1 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Voice Provider"
                    value={formData.voiceProvider}
                    onChange={handleChange("voiceProvider")}
                    helperText={
                      loadingVoices ? "Loading voice providers..." : ""
                    }
                  >
                    {Array.from(
                      new Set(voiceConfigs.map((v) => v.provider))
                    ).map((provider) => (
                      <MenuItem key={provider} value={provider}>
                        {provider}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Voice Name"
                    value={formData.voiceName}
                    onChange={handleChange("voiceName")}
                    helperText={
                      loadingVoices
                        ? "Loading voices..."
                        : "Browse voices in the Voice Library"
                    }
                  >
                    {voiceConfigs
                      .filter(
                        (v) =>
                          v.provider?.toLowerCase() ===
                          formData.voiceProvider?.toLowerCase()
                      )
                      .map((voice) => (
                        <MenuItem key={voice._id} value={voice.voice_name}>
                          {voice.voice_name} — {voice.accent} ({voice.gender})
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography gutterBottom>
                    Speed: {formData.voiceSpeed}x
                  </Typography>
                  <Slider
                    value={formData.voiceSpeed}
                    onChange={handleSliderChange("voiceSpeed")}
                    min={0.5}
                    max={2}
                    step={0.1}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography gutterBottom>
                    Pitch: {formData.voicePitch}
                  </Typography>
                  <Slider
                    value={formData.voicePitch}
                    onChange={handleSliderChange("voicePitch")}
                    min={-20}
                    max={20}
                    step={1}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography gutterBottom>
                    Stability: {formData.voiceStability}
                  </Typography>
                  <Slider
                    value={formData.voiceStability}
                    onChange={handleSliderChange("voiceStability")}
                    min={0}
                    max={1}
                    step={0.1}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined">Browse Voice Library</Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 3: Transcriber */}
          {activeTab === 2 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Transcriber Provider"
                    value={formData.transcriberProvider}
                    onChange={handleChange("transcriberProvider")}
                    helperText={
                      loadingTranscribers ? "Loading providers..." : ""
                    }
                  >
                    {Array.from(
                      new Set(
                        transcriberConfigs.map((t) => t.provider.toLowerCase())
                      )
                    ).map((provider) => (
                      <MenuItem key={provider} value={provider}>
                        {provider.charAt(0).toUpperCase() + provider.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Language"
                    value={formData.language}
                    onChange={handleChange("language")}
                    helperText={
                      loadingTranscribers ? "Loading languages..." : ""
                    }
                  >
                    {Array.from(
                      new Set(
                        transcriberConfigs
                          .filter(
                            (t) =>
                              t.provider.toLowerCase() ===
                              formData.transcriberProvider.toLowerCase()
                          )
                          .map((t) => t.language)
                      )
                    ).map((lang) => (
                      <MenuItem key={lang} value={lang}>
                        {lang.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Model"
                    value={formData.transcriberModel}
                    onChange={handleChange("transcriberModel")}
                    helperText={loadingTranscribers ? "Loading models..." : ""}
                  >
                    {transcriberConfigs
                      .filter(
                        (t) =>
                          t.provider?.toLowerCase() ===
                          formData.transcriberProvider?.toLowerCase()
                      )
                      .map((t) => (
                        <MenuItem key={t._id} value={t.model}>
                          {t.model}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_punctuation_enabled}
                        onChange={handleSwitchChange("is_punctuation_enabled")}
                      />
                    }
                    label="Enable Punctuation"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_profanity_filter_enabled}
                        onChange={handleSwitchChange(
                          "is_profanity_filter_enabled"
                        )}
                      />
                    }
                    label="Enable Profanity Filter"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 4: Tools */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Available Tools
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3, fontSize: "0.875rem" }}>
                Select tools for your assistant to enhance its capabilities
              </Typography>

              {loadingTools ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">Loading tools...</Typography>
                </Box>
              ) : tools.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 6,
                    border: "1px dashed #E0E0E0",
                    borderRadius: "8px",
                  }}
                >
                  <Typography color="text.secondary">
                    No tools available
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {tools.map((tool) => (
                    <Box
                      key={tool._id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2.5,
                        mb: 2,
                        border: tool.status ? "2px solid #667eea" : "1px solid #E5E7EB",
                        borderRadius: "8px",
                        backgroundColor: tool.status ? "#F3F4FF" : "#FFFFFF",
                        transition: "all 0.2s",
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    >
                      <Box sx={{ flexGrow: 1, mr: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                          {tool.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tool.function}
                        </Typography>
                      </Box>
                      <Switch
                        checked={tool.status}
                        onChange={() =>
                          handleToolToggle(tool._id, tool.status)
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#667eea",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#667eea",
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Tab 5: Analysis */}
          {activeTab === 4 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Select Analysis Configuration"
                    value={selectedAnalysis || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedAnalysis(id);
                      const selected = analysisConfigs.find(
                        (a) => a._id === id
                      );
                      if (selected) {
                        setFormData((prev) => ({
                          ...prev,
                          analysis_id: id,
                          enableSummary: selected.enable_auto_summary,
                          enableSentiment: selected.include_sentiment_analysis,
                          enableTopics: selected.extract_key_topics,
                          enableActionItems: selected.extract_action_items,
                        }));
                      }
                    }}
                    helperText={
                      loadingAnalysis
                        ? "Loading analysis configs..."
                        : "Choose a preset analysis configuration"
                    }
                  >
                    {analysisConfigs.map((config) => (
                      <MenuItem key={config._id} value={config._id}>
                        {config.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enableSummary}
                        onChange={handleSwitchChange("enableSummary")}
                      />
                    }
                    label="Enable Auto-Summary"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enableSentiment}
                        onChange={handleSwitchChange("enableSentiment")}
                      />
                    }
                    label="Include Sentiment Analysis"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enableTopics}
                        onChange={handleSwitchChange("enableTopics")}
                      />
                    }
                    label="Extract Key Topics"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enableActionItems}
                        onChange={handleSwitchChange("enableActionItems")}
                      />
                    }
                    label="Extract Action Items"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 6: Advanced Settings */}
          {activeTab === 5 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Privacy & Compliance
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hipaaCompliance}
                        onChange={handleSwitchChange("hipaaCompliance")}
                      />
                    }
                    label="HIPAA Compliance Mode"
                  />
                  {formData.hipaaCompliance && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      display="block"
                    >
                      Warning: When enabled, no logs, recordings, or
                      transcriptions will be stored unless custom storage is
                      configured.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Audio Recording Format"
                    value={formData.recordingFormat}
                    onChange={handleChange("recordingFormat")}
                  >
                    <MenuItem value="wav">WAV</MenuItem>
                    <MenuItem value="mp3">MP3</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Data Retention Period"
                    value={formData.dataRetention}
                    onChange={handleChange("dataRetention")}
                  >
                    <MenuItem value="7">7 Days</MenuItem>
                    <MenuItem value="30">30 Days</MenuItem>
                    <MenuItem value="90">90 Days</MenuItem>
                    <MenuItem value="forever">Forever</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Call Duration (minutes)"
                    value={formData.maxCallDuration}
                    onChange={handleChange("maxCallDuration")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Silence Timeout (seconds)"
                    value={formData.silenceTimeout}
                    onChange={handleChange("silenceTimeout")}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 7: Widget */}
          {activeTab === 6 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Widget Configuration
              </Typography>

              {/* Section 1: Website URLs */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Website URLs
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: "0.875rem" }}>
                  Add website URLs to extract content for your assistant
                </Typography>

                {/* Add URL Input */}
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Website URL"
                    placeholder="https://example.com"
                    value={formData.widgetUrl}
                    onChange={handleChange("widgetUrl")}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleAddWebsiteUrl();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddWebsiteUrl}
                    sx={{
                      minWidth: 120,
                      backgroundColor: "#667eea",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#5568d3",
                      },
                    }}
                  >
                    Add URL
                  </Button>
                </Box>

                {/* Display Website URLs */}
                {formData.sources.filter((s) => s.type === "website").length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Added URLs:
                    </Typography>
                    {formData.sources.map((source, index) => (
                      source.type === "website" && (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            mb: 1,
                            backgroundColor: "#F9FAFB",
                            borderRadius: 1,
                            border: "1px solid #E0E0E0",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                backgroundColor: "#E3F2FD",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography sx={{ fontSize: 16 }}>🌐</Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {source.url}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveSource(index)}
                            sx={{ color: "error.main" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )
                    ))}
                  </Box>
                )}
              </Box>

              {/* Section 2: Uploaded Files */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Knowledge Base Files
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: "0.875rem" }}>
                  Select from existing knowledge base files or upload new documents
                </Typography>

                {/* Knowledge Base Files Listing */}
                {loadingKnowledgeBase ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading knowledge base files...
                    </Typography>
                  </Box>
                ) : knowledgeBaseFiles.length > 0 ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Select from Knowledge Base:
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: 300,
                        overflowY: "auto",
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      {knowledgeBaseFiles.map((file) => (
                        <Box
                          key={file.id}
                          onClick={() => handleKnowledgeBaseFileToggle(file)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            mb: 1,
                            backgroundColor: isKnowledgeBaseFileSelected(file.id)
                              ? "#E3F2FD"
                              : "#F9FAFB",
                            borderRadius: 1,
                            border: isKnowledgeBaseFileSelected(file.id)
                              ? "2px solid #1976D2"
                              : "1px solid #E0E0E0",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            "&:hover": {
                              backgroundColor: isKnowledgeBaseFileSelected(file.id)
                                ? "#E3F2FD"
                                : "#F0F0F0",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                backgroundColor: isKnowledgeBaseFileSelected(file.id)
                                  ? "#1976D2"
                                  : "#FFF3E0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography sx={{ fontSize: 16 }}>
                                {isKnowledgeBaseFileSelected(file.id) ? "✓" : "📄"}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {file.type} • {file.size} • {file.status}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 3,
                      mb: 3,
                      border: "1px dashed #E0E0E0",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No files in knowledge base. Upload files below to get started.
                    </Typography>
                  </Box>
                )}

                {/* Display Existing Files from API */}
                {formData.sources.filter((s) => s.type === "file").length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Uploaded Files:
                    </Typography>
                    {formData.sources.map((source, index) => (
                      source.type === "file" && (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            mb: 1,
                            backgroundColor: "#F9FAFB",
                            borderRadius: 1,
                            border: "1px solid #E0E0E0",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                backgroundColor: "#FFF3E0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography sx={{ fontSize: 16 }}>📄</Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={600}>
                              {source.name || "Untitled"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewFile(source)}
                            >
                              View
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveSource(index)}
                              sx={{ color: "error.main" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      )
                    ))}
                  </Box>
                )}

                {/* Upload New File */}
                <Box>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<AddIcon />}
                    sx={{
                      borderRadius: "8px",
                      borderColor: "#667eea",
                      color: "#667eea",
                      py: 1.5,
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "#5568d3",
                        backgroundColor: "rgba(102, 126, 234, 0.04)",
                      },
                    }}
                  >
                    Upload New File
                    <input
                      type="file"
                      hidden
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.txt"
                    />
                  </Button>

                  {/* Show Newly Selected File */}
                  {formData.widgetFileName && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        backgroundColor: "#E8F5E9",
                        borderRadius: 1,
                        border: "1px solid #4CAF50",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                          <Typography sx={{ fontSize: 16 }}>✓</Typography>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              New file selected: {formData.widgetFileName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              This file will be uploaded when you save the assistant
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={handleRemoveSelectedFile}
                          sx={{ color: "error.main" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Test Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={handleTestDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assistant Saved Successfully</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Your assistant has been saved as a draft. You can now test it or
            continue editing.
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Assistant Details:
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {formData.name}
            </Typography>
            <Typography variant="body2">
              <strong>Status:</strong> Draft
            </Typography>
            <Typography variant="body2">
              <strong>Tools Selected:</strong>{" "}
              {tools.filter((t) => t.status).length}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestDialogClose} variant="outlined">
            Continue Editing
          </Button>
          <Button onClick={() => navigate("/assistants")} variant="contained">
            Go to Assistants
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssistantConfiguration;
